import { isEqual } from "lodash-es";
import { client } from "./nats-client";

function createNewUser(User, AvatarColor, UserColors, sessionId) {
  const color = getColor(AvatarColor, UserColors);
  const time = new Date().toISOString();
  return { Name: User.Name, Email: User.Email, IsActive: false, Id: sessionId, Time: time, Color: color };
}

function publishUser(stream, pageId, users, colors, sessionId) {
  const time = new Date().toISOString();
  client.putKvJetstream(stream, pageId, "user", {
    clientId: client.getId(),
    sessionId,
    source: "User",
    users: JSON.stringify(users),
    time,
    colors,
  });
}

function getColor(AvatarColor, UserColors) {
  const colors = UserColors.peek();

  function generateAvatarColor(existingColors: string[], hueThreshold = 30): string {
    const saturation = 60;
    const lightness = 65;
    const MAX_ATTEMPTS = 100; // prevent infinite loop

    const extractHue = (hsl: string): number => {
      const match = hsl.match(/hsl\((\d+),/);
      return match ? parseInt(match[1], 10) : -1;
    };

    const isHueSimilar = (hue1: number, hue2: number, threshold: number): boolean => {
      const diff = Math.abs(hue1 - hue2);
      return diff < threshold || 360 - diff < threshold;
    };

    let hue: number;
    let color: string;
    let attempts = 0;

    do {
      hue = Math.floor(Math.random() * 360);
      color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      attempts++;
    } while (
      existingColors.some((existingColor) => isHueSimilar(hue, extractHue(existingColor), hueThreshold)) &&
      attempts < MAX_ATTEMPTS
    );

    return color;
  }

  function setColor() {
    const existingColors = ["hsl(120, 60.8%, 50%)", ...colors];
    return generateAvatarColor(existingColors);
  }

  if (!AvatarColor.peek()) {
    let color = sessionStorage.getItem(`avatar-color`);
    if (!color) {
      color = setColor();
      sessionStorage.setItem(`avatar-color`, color);
    }
    AvatarColor.value = color;
  }

  if (!colors.includes(AvatarColor.peek())) {
    UserColors.value = [...colors, AvatarColor];
  }

  return AvatarColor.peek();
}

function setActive(Users, sessionId, time, INACTIVE_LIMIT) {
  let users = JSON.parse(JSON.stringify(Users.value));
  const index = users.findIndex((item) => item.Id === sessionId);
  if (index >= 0) {
    const user = users[index];
    user.Active = new Date().toISOString();
    user.IsActive = true;

    Users.value = users;
  }
}

export function getTimeDifference(after, before) {
  return after - before;
}
function getDifferenceInMinute(after, before) {
  return Math.floor(getTimeDifference(after, before) / 60000);
}

function addToUsers(Users, u, INACTIVE_LIMIT, UNRESPONSIVE_LIMIT) {
  const currTime: Date = new Date();
  let users = JSON.parse(JSON.stringify(Users.value));

  u.forEach((user) => {
    const userTime: Date = new Date(user.Time);
    const timeDifference = getTimeDifference(currTime, userTime);
    const index = users.findIndex((item) => item.Id === user.Id);

    if (index === -1 && timeDifference < UNRESPONSIVE_LIMIT) {
      const IsActive = user.IsActive && getTimeDifference(new Date(), new Date(user.Active)) < INACTIVE_LIMIT;
      users = [...users, { ...user, IsActive }];
    } else if (index >= 0) {
      const originalTime: Date = new Date(users[index].Time);
      // Update iff incoming time is newer than saved time
      if (Math.floor(getTimeDifference(userTime, originalTime)) > 0) {
        users[index].Time = user.Time;
      }
    }
  });

  if (!isEqual(Users.peek(), users)) {
    Users.value = users;
  }
}

function deleteInactiveUsers(Users, UserColors, OverlayCollection, sessionId, INACTIVE_LIMIT, UNRESPONSIVE_LIMIT) {
  const activeUsers = Users.peek();
  let users = JSON.parse(JSON.stringify(activeUsers));

  activeUsers.forEach((user, index) => {
    const nonresponsiveTime = getTimeDifference(new Date(), new Date(user.Time));

    if (user.IsActive) {
      if (getTimeDifference(new Date(), new Date(user.Active)) >= INACTIVE_LIMIT) {
        const newUser = { ...user, IsActive: false };
        users = [...users.slice(0, index), newUser, ...users.slice(index + 1)];
      }
    }

    if (nonresponsiveTime >= UNRESPONSIVE_LIMIT) {
      if (!(user.Id === sessionId)) {
        users = users.filter((item) => item.Id !== user.Id);

        const overlayCollection = JSON.parse(JSON.stringify(OverlayCollection.peek()));
        if (overlayCollection[user.Id]) delete overlayCollection[user.Id];
        OverlayCollection.value = overlayCollection;
      }
    }
  });

  if (!isEqual(activeUsers, users)) {
    Users.value = users;
  }
}

export const users = {
  createNewUser: createNewUser,
  deleteInactiveUsers: deleteInactiveUsers,
  addToUsers: addToUsers,
  setActive: setActive,
  publishUser: publishUser,
};
